(() => {
  const day = 86400000;
  const civilFormatters = new Map();
  const holidayCache = new Map();

  function validTimezone(value = 'Europe/Paris') {
    try {
      return new Intl.DateTimeFormat('en', { timeZone: value }).resolvedOptions().timeZone;
    } catch {
      return 'Europe/Paris';
    }
  }

  function validLocale(value = 'fr-FR') {
    try {
      return new Intl.DateTimeFormat(value).resolvedOptions().locale;
    } catch {
      return 'fr-FR';
    }
  }

  function civilDate(instant = Date.now(), timezone = 'Europe/Paris') {
    timezone = validTimezone(timezone);
    if (!civilFormatters.has(timezone)) civilFormatters.set(timezone, new Intl.DateTimeFormat('en-CA', { timeZone: timezone, calendar: 'gregory', numberingSystem: 'latn', year: 'numeric', month: '2-digit', day: '2-digit' }));
    const values = Object.fromEntries(civilFormatters.get(timezone).formatToParts(new Date(instant)).filter(part => ['year', 'month', 'day'].includes(part.type)).map(part => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  }

  function dateValue(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) throw new RangeError('Invalid civil date');
    const value = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (new Date(value).toISOString().slice(0, 10) !== iso) throw new RangeError('Invalid civil date');
    return value;
  }

  function shiftDate(iso, amount) {
    return new Date(dateValue(iso) + amount * day).toISOString().slice(0, 10);
  }

  function easterDate(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const date = (h + l - 7 * m + 114) % 31 + 1;
    return `${year}-${String(month).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
  }

  function holidayDates(year) {
    if (!holidayCache.has(year)) {
      const easter = easterDate(year);
      const fixed = ['01-01', '05-01', '05-08', '07-14', '08-15', '11-01', '11-11', '12-25'].map(date => `${year}-${date}`);
      holidayCache.set(year, Object.freeze([...fixed, shiftDate(easter, 1), shiftDate(easter, 39), shiftDate(easter, 50)].sort()));
    }
    return holidayCache.get(year);
  }

  function isBusinessDay(iso, holidays = 'fr') {
    const weekday = new Date(dateValue(iso)).getUTCDay();
    return weekday !== 0 && weekday !== 6 && (holidays === 'none' || !holidayDates(Number(iso.slice(0, 4))).includes(iso));
  }

  function addBusinessDays(iso, amount, holidays = 'fr') {
    if (!Number.isInteger(amount) || amount < 0 || amount > 180) throw new RangeError('Invalid business day count');
    let current = iso;
    dateValue(current);
    let remaining = amount;
    while (remaining > 0) {
      current = shiftDate(current, 1);
      if (isBusinessDay(current, holidays)) remaining--;
    }
    return current;
  }

  function normalizeOptions(options = {}) {
    const number = (value, fallback) => {
      const parsed = Number(value);
      return Number.isInteger(parsed) && parsed >= 1 && parsed <= 180 ? parsed : fallback;
    };
    const processingMin = number(options.processingMin, 1);
    const processingMax = Math.max(processingMin, number(options.processingMax, 3));
    const deliveryMin = Math.max(processingMin, number(options.deliveryMin, 7));
    const deliveryMax = Math.max(deliveryMin, processingMax, number(options.deliveryMax, 12));
    return { timezone: validTimezone(options.timezone || options.timeZone), locale: validLocale(options.locale), holidays: options.holidays === 'none' ? 'none' : 'fr', processingMin, processingMax, deliveryMin, deliveryMax };
  }

  function calculate(instant = Date.now(), options = {}) {
    const settings = normalizeOptions(options);
    const ordered = civilDate(instant, settings.timezone);
    return {
      ordered,
      dispatched: { min: addBusinessDays(ordered, settings.processingMin, settings.holidays), max: addBusinessDays(ordered, settings.processingMax, settings.holidays) },
      delivered: { min: shiftDate(ordered, settings.deliveryMin), max: shiftDate(ordered, settings.deliveryMax) },
      settings
    };
  }

  function formatDate(iso, locale = 'fr-FR', includeYear = false) {
    return new Intl.DateTimeFormat(validLocale(locale), { day: 'numeric', month: 'short', ...(includeYear ? { year: 'numeric' } : {}), timeZone: 'UTC' }).format(new Date(dateValue(iso)));
  }

  function formatRange(min, max, locale = 'fr-FR') {
    if (min === max) return formatDate(min, locale);
    const includeYear = min.slice(0, 4) !== max.slice(0, 4);
    const formatter = new Intl.DateTimeFormat(validLocale(locale), { day: 'numeric', month: 'short', ...(includeYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
    if (typeof formatter.formatRange === 'function') return formatter.formatRange(new Date(dateValue(min)), new Date(dateValue(max)));
    return `${formatDate(min, locale, includeYear)} – ${formatDate(max, locale, includeYear)}`;
  }

  function untilNextMidnight(instant = Date.now(), timezone = 'Europe/Paris') {
    const now = new Date(instant).getTime();
    const currentDate = civilDate(now, timezone);
    let low = now;
    let high = now + 36 * 3600000;
    while (high - low > 1) {
      const middle = Math.floor((low + high) / 2);
      if (civilDate(middle, timezone) === currentDate) low = middle;
      else high = middle;
    }
    return Math.max(1000, high - now + 100);
  }

  globalThis.SCPurchaseTimelineCalendar = Object.freeze({ civilDate, holidayDates, isBusinessDay, addBusinessDays, normalizeOptions, calculate, formatDate, formatRange, untilNextMidnight });

  if (!customElements.get('sc-purchase-timeline')) {
    customElements.define('sc-purchase-timeline', class extends HTMLElement {
      static get observedAttributes() {
        return ['data-processing-min', 'data-processing-max', 'data-delivery-min', 'data-delivery-max', 'data-timezone', 'data-locale', 'data-holidays'];
      }

      connectedCallback() {
        this.abort?.abort();
        clearTimeout(this.midnightTimer);
        this.abort = new AbortController();
        const options = { signal: this.abort.signal };
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) clearTimeout(this.midnightTimer);
          else this.refresh();
        }, options);
        window.addEventListener('pageshow', () => this.refresh(), options);
        this.refresh();
      }

      attributeChangedCallback() {
        if (this.isConnected && this.abort) this.refresh();
      }

      refresh() {
        if (!this.isConnected) return;
        clearTimeout(this.midnightTimer);
        const instant = Date.now();
        const timeline = calculate(instant, this.dataset);
        const ordered = this.querySelector('[data-sc-order-date]');
        const dispatched = this.querySelector('[data-sc-dispatch-date]');
        const delivered = this.querySelector('[data-sc-delivery-date]');
        if (ordered) {
          ordered.textContent = formatDate(timeline.ordered, timeline.settings.locale);
          ordered.dataset.date = timeline.ordered;
          if (ordered.tagName === 'TIME') ordered.dateTime = timeline.ordered;
        }
        for (const [node, interval] of [[dispatched, timeline.dispatched], [delivered, timeline.delivered]]) {
          if (!node) continue;
          node.textContent = formatRange(interval.min, interval.max, timeline.settings.locale);
          node.dataset.minDate = interval.min;
          node.dataset.maxDate = interval.max;
        }
        this.dataset.ready = 'true';
        if (!document.hidden) {
          const delay = Math.max(1, untilNextMidnight(instant, timeline.settings.timezone) - (Date.now() - instant));
          this.midnightTimer = setTimeout(() => this.refresh(), delay);
        }
      }

      disconnectedCallback() {
        clearTimeout(this.midnightTimer);
        this.abort?.abort();
      }
    });
  }
})();
